'use client'
import {
  navConfig,
  navConfigCareAndShare,
  navConfigCollection,
} from '@/components/layout/header/nav-config'
import { Button } from '@/components/ui/button'
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn, stringToSlug } from '@/lib/utils'
import { MoveRight } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'

interface NavItemProps {
  href: string
  title: string
  className?: string
  isComingSoon?: boolean
  description?: string
}

interface NavSectionProps {
  section: {
    title: string
    href?: string
    items: NavItemProps[]
  }
}

const NavItem = ({
  href,
  title,
  className,
  isComingSoon,
  description,
}: NavItemProps) => {
  const t = useTranslations('header')
  return (
    <li className='block h-fit w-fit'>
      <Link
        href={href}
        aria-disabled={isComingSoon}
        tabIndex={isComingSoon ? -1 : 0}
        className={cn(
          'group line-clamp-2 whitespace-nowrap text-xs text-neutral-600 transition-colors duration-200 hover:text-primary xl:text-sm 2xl:text-base',
          className,
          isComingSoon &&
            'pointer-events-none cursor-default text-neutral-400 hover:text-neutral-400'
        )}
        onClick={(e) => isComingSoon && e.preventDefault()}
        aria-label={
          isComingSoon ? `${t(title)} (Sắp ra mắt)` : `Đi tới: ${t(title)}`
        }
        data-tracking-level-4={`_${stringToSlug(title)}`}
      >
        {t(title)}
        {isComingSoon && <p className='italic'>(Coming soon)</p>}
        {description && <p className='text-sm italic'>{description}</p>}
      </Link>
    </li>
  )
}

const NavSection = ({ section }: NavSectionProps) => {
  const t = useTranslations('header')
  return (
    <ul className='space-y-2 2xl:space-y-5'>
      <li>
        <Link
          href={section?.href || '/'}
          className={cn('w-fit', !section?.href && 'cursor-default')}
          onClick={(e) => !section?.href && e.preventDefault()}
          aria-label={section.href ? `Đi tới: ${section.title}` : undefined}
          data-tracking-level-4={`_${stringToSlug(section.title)}`}
        >
          <h4
            className={cn(
              'group flex items-center whitespace-nowrap text-sm font-bold uppercase text-neutral-900 transition-colors duration-200 2xl:text-base',
              section.href && 'hover:text-primary'
            )}
          >
            {t(`${section.title}`)}
            {section?.href && (
              <MoveRight className='ml-3 text-primary transition-transform duration-300 group-hover:translate-x-2 max-2xl:h-4 max-2xl:w-4' />
            )}
          </h4>
        </Link>
      </li>
      {section.items.map((item) => (
        <NavItem key={item.href} {...item} />
      ))}
    </ul>
  )
}

const FeaturedContent = ({
  featured,
  className,
}: {
  featured?: {
    title: string
    href: string
    imageAlt: string
    imageSrc: string
    tag?: string
  }[]
  className?: string
}) => {
  const t = useTranslations('header')

  return (
    <div
      className={cn(
        'flex basis-[28%] flex-col gap-5 border-l border-l-neutral-300 px-8 py-8 2xl:px-10 2xl:pb-17.5',
        className
      )}
    >
      {featured?.map((item) => (
        <Link
          key={item.href}
          href={item.href}
          className='group relative flex-1 overflow-hidden rounded-lg'
        >
          <Image
            width={340}
            height={185}
            src={ImageHelper.getImageUrl(item.imageSrc)}
            alt={item.title || 'Hình ảnh nổi bật'}
            className='aspect-[340/185] h-full w-full object-cover transition-transform duration-300 ease-in-out hover:scale-105 xl:h-full'
          />
          <p className='absolute bottom-3 left-4 line-clamp-2 font-sans text-sm font-bold text-background 2xl:text-lg'>
            {t(item.title)}
          </p>
          {item.tag && (
            <p className='absolute right-4 top-3 rounded-full bg-red-600 px-2 py-0.5 text-xs font-medium text-background'>
              {item.tag}
            </p>
          )}
        </Link>
      ))}
    </div>
  )
}

export function MainNav() {
  const [value, setValue] = useState('')
  const pathname = usePathname()

  useEffect(() => {
    setValue('')
  }, [pathname])

  const t = useTranslations('header')

  return (
    <nav
      aria-label='Thanh điều hướng chính'
      className='hidden h-full items-center lg:block'
    >
      <NavigationMenu className='z-[60]' value={value} onValueChange={setValue}>
        <NavigationMenuList role='menu'>
          <NavigationMenuItem role='menuitem'>
            <Link
              href='/collection/san-pham-moi?itm_source=navbar'
              className={cn(
                navigationMenuTriggerStyle(),
                'relative font-medium text-primary before:bg-primary'
              )}
            >
              {t('nav.section.new_product')}
            </Link>
          </NavigationMenuItem>

          {Object.entries(navConfig).map(([category, content]) => (
            <NavigationMenuItem role='menuitem' key={category}>
              <NavigationMenuTrigger asChild>
                <Link
                  href={content.href}
                  className={navigationMenuTriggerStyle()}
                  aria-label={`Mở Trang: ${category}`}
                  data-tracking-level-2={`menu__main-${stringToSlug(category)}`}
                >
                  {t(category)}
                </Link>
              </NavigationMenuTrigger>

              <NavigationMenuContent
                className='w-full'
                data-tracking-level-3={`menu__main-${stringToSlug(category)}`}
              >
                <div className='flex w-full bg-background'>
                  <div className='flex basis-[72%] flex-col justify-between'>
                    <div className='flex justify-between space-x-6 px-10 pb-8 pt-8 2xl:space-x-16 2xl:px-20 2xl:pb-17.5'>
                      {content.sections.map((section, idx) => (
                        <NavSection key={idx} section={section} />
                      ))}
                    </div>

                    {content?.footer?.map((footerItem, idx) => (
                      <ul
                        className='flex items-center bg-neutral-100 px-20'
                        key={idx}
                      >
                        <li className='border-r border-r-neutral-200 px-6 py-6 font-sans text-sm uppercase text-neutral-500'>
                          {t(footerItem?.title)}
                        </li>
                        {footerItem?.items.map((footerItemSub) => (
                          <li key={footerItemSub.href}>
                            <Link
                              data-tracking-level-3={`_${stringToSlug(category)}`}
                              href={footerItemSub.href}
                              className='line-clamp-1 px-6 py-6 font-sans text-base font-medium uppercase text-neutral-900 transition-colors hover:text-primary'
                            >
                              {t(footerItemSub.title)}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    ))}
                  </div>

                  <FeaturedContent featured={content?.featured} />
                </div>
              </NavigationMenuContent>
            </NavigationMenuItem>
          ))}

          {Object.entries(navConfigCollection).map(([category, content]) => (
            <NavigationMenuItem role='menuitem' key={category}>
              <NavigationMenuTrigger asChild>
                <Link
                  href={content.href}
                  className={navigationMenuTriggerStyle()}
                  aria-label={`Mở menu danh mục: ${category}`}
                  data-tracking-level-3={stringToSlug(category)}
                >
                  {t(category)}
                </Link>
              </NavigationMenuTrigger>

              <NavigationMenuContent className='w-full'>
                <div className='flex w-full bg-background'>
                  <div className='flex flex-1'>
                    {content.sections.map((section, idx) => (
                      <div
                        key={idx}
                        className='border-l-neutral-300 px-10 pb-8 pt-8 last-of-type:border-l 2xl:px-20 2xl:pb-17.5'
                      >
                        <Link
                          href={section?.href || '/'}
                          className={cn(
                            'w-fit',
                            !section?.href && 'cursor-default'
                          )}
                          onClick={(e) => !section?.href && e.preventDefault()}
                          data-tracking-level-3={stringToSlug(category)}
                        >
                          <h4
                            className={cn(
                              'group flex items-center whitespace-nowrap text-sm font-bold uppercase text-neutral-900 transition-colors duration-300 2xl:text-base',
                              section.href && 'hover:text-primary'
                            )}
                          >
                            {t(section.title)}
                            {section?.href && (
                              <MoveRight className='ml-3 text-primary transition-transform duration-200 group-hover:translate-x-2' />
                            )}
                          </h4>
                        </Link>
                        <ul className='mt-4 grid flex-1 grid-cols-2 gap-x-8 gap-y-3 2xl:gap-x-10 2xl:gap-y-5'>
                          {section.items.map((item) => (
                            <li key={item.href} className='block h-fit'>
                              <Link
                                href={item.href}
                                className='group line-clamp-2 flex w-full items-center gap-3 whitespace-nowrap text-base text-neutral-600 transition-colors duration-200 hover:text-primary'
                                data-tracking-level-3={stringToSlug(category)}
                              >
                                <Image
                                  src={ImageHelper.getImageUrl(item.img)}
                                  width={48}
                                  height={48}
                                  alt={item.title}
                                  className='h-9 w-9 rounded-full object-cover transition-transform duration-300 ease-in-out group-hover:scale-95 2xl:h-12 2xl:w-12'
                                />
                                <p className='w-full flex-1 border-b border-b-neutral-100 py-2 font-sans text-xs text-neutral-600 group-hover:text-primary md:text-sm 2xl:py-4 2xl:text-base'>
                                  {t(item.title)}
                                </p>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>

                  <div
                    className={cn(
                      'grid basis-[28%] gap-5 border-l border-l-neutral-300 px-8 pb-6 pt-8 xl:pb-12 xl:pt-10 2xl:px-20',
                      content?.featured?.length === 2 &&
                        content.sections?.length >= 1
                        ? 'grid-cols-1'
                        : 'grid-cols-3'
                    )}
                  >
                    {content?.featured?.map((item) => (
                      <Link
                        href={item.href}
                        className='group relative overflow-hidden rounded-lg'
                        key={item.href}
                      >
                        <Image
                          width={340}
                          height={185}
                          src={ImageHelper.getImageUrl(item.imageSrc)}
                          alt={item.title || 'Hình ảnh nổi bật'}
                          className='aspect-[340/185] h-full w-full object-cover transition-transform duration-300 ease-in-out hover:scale-105 xl:h-full'
                        />
                        <p className='absolute bottom-3 left-4 line-clamp-2 font-sans text-sm font-bold text-background 2xl:text-lg'>
                          {t(item.title)}
                        </p>
                      </Link>
                    ))}
                  </div>
                </div>
              </NavigationMenuContent>
            </NavigationMenuItem>
          ))}

          <NavigationMenuItem role='menuitem'>
            <Link
              href='/collection/giam-gia?itm_source=navbar'
              className={cn(
                navigationMenuTriggerStyle(),
                'relative font-medium text-red-600 before:bg-red-600'
              )}
            >
              <p className='absolute right-3 top-2 text-xxs text-red-600'>
                -50%
              </p>
              {t('nav.section.outlet')}
            </Link>
          </NavigationMenuItem>

          <NavigationMenuItem role='menuitem'>
            <NavigationMenuTrigger asChild>
              <Link
                href={navConfigCareAndShare.href}
                className={navigationMenuTriggerStyle()}
                aria-label={`Đi tới trang ${t(navConfigCareAndShare.title)}`}
                data-tracking-level-3={stringToSlug(
                  t(navConfigCareAndShare.title)
                )}
              >
                {t(navConfigCareAndShare.title)}
              </Link>
            </NavigationMenuTrigger>

            <NavigationMenuContent className='flex w-full bg-background'>
              <div className='flex flex-1 justify-end px-20 pb-12 pt-10'>
                <div className='flex max-w-[720px] flex-col items-center'>
                  <p className='text- max-w-[600px] text-center font-sans text-base text-neutral-900/70'>
                    {t(navConfigCareAndShare.description)}
                  </p>

                  <Image
                    src={ImageHelper.getImageUrl(
                      navConfigCareAndShare.imageStep
                    )}
                    alt={navConfigCareAndShare.title || 'Các bước xử lý'}
                    width={725}
                    height={110}
                    unoptimized
                    className='mt-5 w-full object-contain 2xl:max-h-[150px] 2xl:object-contain'
                  />

                  <Button className='group mt-8' size='large' asChild>
                    <Link href={navConfigCareAndShare.href}>
                      {t(navConfigCareAndShare.buttonText)}
                      <MoveRight className='text-light transition-transform duration-300 group-hover:translate-x-2' />
                    </Link>
                  </Button>
                </div>
              </div>

              <div className='grid basis-2/5 gap-5 border-l border-l-neutral-300 px-8 pb-6 pt-8 xl:pb-12 xl:pt-10 2xl:px-20'>
                <Image
                  width={450}
                  height={262}
                  src={ImageHelper.getImageUrl(navConfigCareAndShare.image)}
                  alt={navConfigCareAndShare.title || 'Hình ảnh nổi bật'}
                  className='aspect-[340/185] h-full w-full rounded-lg object-cover'
                />
              </div>
            </NavigationMenuContent>
          </NavigationMenuItem>
        </NavigationMenuList>
      </NavigationMenu>
    </nav>
  )
}
